import React from "react";
import { ASSETS } from "../../lib/api";
import OrnamentalDivider from "./OrnamentalDivider";

const titles = [
    "Gana Visharada",
    "Sangeetha Shree",
    "Gana Chatura",
    "Karnataka Vikasaratna",
    "Havyakashree",
];

const albums = [
    "Ananya Bhava",
    "He Jeevana Yatraanta",
    "Yantra Rishi",
    "Anarghya Bhakti",
];

export default function Founder() {
    return (
        <section
            id="founder"
            data-testid="founder-section"
            className="section-pad bg-ivory"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5 order-2 lg:order-1">
                        <div className="relative max-w-md mx-auto lg:mx-0 isolate">
                            {/* Decorative backdrop — maroon panel + mandala + gold halo */}
                            <div className="absolute -inset-x-8 -top-6 -bottom-12 lg:-inset-x-10 lg:-top-10 lg:-bottom-16 bg-gradient-to-br from-maroon-900 via-maroon to-maroon-900 z-0" aria-hidden="true" />
                            <div
                                className="absolute -inset-x-8 -top-6 -bottom-12 lg:-inset-x-10 lg:-top-10 lg:-bottom-16 opacity-25 z-0 pointer-events-none"
                                aria-hidden="true"
                                style={{
                                    backgroundImage:
                                        "radial-gradient(rgba(221,183,113,0.6) 1px, transparent 1px)",
                                    backgroundSize: "5px 5px",
                                }}
                            />
                            {/* Rotating mandala behind portrait */}
                            <svg
                                viewBox="0 0 200 200"
                                className="absolute -top-4 -right-4 w-40 h-40 lg:w-52 lg:h-52 z-0 opacity-80"
                                style={{ animation: "spin 80s linear infinite" }}
                                aria-hidden="true"
                            >
                                <circle cx="100" cy="100" r="92" fill="none" stroke="#B58A43" strokeWidth="0.4" />
                                <circle cx="100" cy="100" r="80" fill="none" stroke="#DDB771" strokeWidth="0.3" strokeDasharray="1 3" />
                                {Array.from({ length: 16 }).map((_, i) => {
                                    const angle = (i * 360) / 16;
                                    return (
                                        <g key={i} transform={`rotate(${angle} 100 100)`}>
                                            <path d="M100 6 L103 16 L100 26 L97 16 Z" fill="#DDB771" />
                                        </g>
                                    );
                                })}
                            </svg>
                            <svg
                                viewBox="0 0 200 200"
                                className="absolute -bottom-4 -left-4 w-32 h-32 lg:w-40 lg:h-40 z-0 opacity-70"
                                style={{ animation: "spin 100s linear infinite reverse" }}
                                aria-hidden="true"
                            >
                                <circle cx="100" cy="100" r="92" fill="none" stroke="#DDB771" strokeWidth="0.4" />
                                {Array.from({ length: 12 }).map((_, i) => {
                                    const angle = (i * 360) / 12;
                                    return (
                                        <g key={i} transform={`rotate(${angle} 100 100)`}>
                                            <circle cx="100" cy="10" r="2" fill="#B58A43" />
                                        </g>
                                    );
                                })}
                            </svg>

                            {/* Spotlight glow */}
                            <div className="absolute inset-0 bg-gradient-to-br from-gold/30 via-transparent to-transparent blur-3xl z-0" aria-hidden="true" />

                            {/* Photo wrapper with frame */}
                            <div className="relative z-10">
                                <div className="absolute -inset-2 border border-gold pointer-events-none" />
                                <div className="absolute -inset-[14px] border border-gold/30 pointer-events-none" />

                                {/* Corner ornaments */}
                                {["-top-3 -left-3", "-top-3 -right-3", "-bottom-3 -left-3", "-bottom-3 -right-3"].map((pos, i) => (
                                    <svg
                                        key={i}
                                        className={`absolute ${pos} w-7 h-7 text-gold drop-shadow-[0_0_8px_rgba(221,183,113,0.6)] pointer-events-none`}
                                        viewBox="0 0 22 22"
                                    >
                                        <path d="M11 1 L13 9 L21 11 L13 13 L11 21 L9 13 L1 11 L9 9 Z" fill="currentColor" />
                                    </svg>
                                ))}

                                <img
                                    src={ASSETS.founder}
                                    alt="Shri Ananya Bhargava Bedur, Founder"
                                    className="relative w-full h-auto object-cover shadow-[0_30px_60px_-15px_rgba(0,0,0,0.55)]"
                                    data-testid="founder-portrait"
                                />
                            </div>

                            {/* Caption ribbon */}
                            <div className="relative z-10 -mt-8 mx-6 bg-ivory border border-gold/50 px-5 py-4 text-center shadow-[0_15px_30px_-15px_rgba(0,0,0,0.5)]">
                                <div className="eyebrow text-gold">· Founder · ಸಂಸ್ಥಾಪಕರು ·</div>
                                <div className="font-display text-xl text-maroon mt-1 leading-tight">
                                    Shri Ananya Bhargava Bedur
                                </div>
                                <div className="text-kannada text-sm text-ink/85 mt-0.5">
                                    ಶ್ರೀ ಅನನ್ಯ ಭಾರ್ಗವ ಬೇದೂರು
                                </div>
                            </div>

                            <div className="relative z-10 mt-10 border-l-2 border-gold-300 pl-5 ml-2 mr-2 lg:ml-0">
                                <div className="eyebrow text-gold-300">Titles Conferred</div>
                                <ul className="mt-3 space-y-1.5 font-display text-lg text-ivory">
                                    {titles.map((t) => (
                                        <li key={t}>· {t}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7 order-1 lg:order-2">
                        <div className="eyebrow">· The Founder ·</div>
                        <h2 className="font-display text-4xl sm:text-5xl text-maroon mt-4 leading-tight">
                            Shri Ananya Bhargava
                            <span className="block italic text-gold text-3xl sm:text-4xl mt-2">
                                Bedur
                            </span>
                        </h2>

                        <div className="mt-8 space-y-5 font-serif text-lg text-ink/85 leading-relaxed">
                            <div className="float-none md:float-right md:ml-8 md:mb-4 md:w-2/5 relative">
                                <div className="absolute -inset-2 border border-gold/40 pointer-events-none" />
                                <img
                                    src={ASSETS.founderTanpura}
                                    alt="Shri Ananya Bhargava Bedur seated with tanpura, tabla and harmonium in his riyaz room"
                                    data-testid="founder-tanpura-image"
                                    className="relative w-full h-auto object-cover shadow-[0_25px_50px_-20px_rgba(0,0,0,0.55)]"
                                />
                                <div className="relative bg-ivory border border-gold/40 px-4 py-3 mt-3 text-center">
                                    <div className="eyebrow text-gold">· In the Riyaz Room ·</div>
                                    <div className="font-display italic text-maroon text-base leading-snug mt-1">
                                        With his tanpura — where every dawn begins.
                                    </div>
                                </div>
                            </div>
                            <p>
                                Hailing from the small village of <em>Beduru</em> in Sagara
                                Taluk of Shivamogga district, Karnataka, Shri Ananya Bhargava
                                has made a significant mark in the field of music through
                                his dedicated efforts.
                            </p>
                            <p>
                                He completed his primary training under{" "}
                                <span className="text-maroon">Smt. Vasudha Sharma</span>, and
                                later pursued Hindustani classical music in the traditional
                                Gurukula system under the renowned vocalist{" "}
                                <span className="text-maroon">
                                    Pt. Ganapati Bhat Hasanagi
                                </span>
                                , eventually earning the <em>Vidwat</em> qualification.
                            </p>
                            <p>
                                He later moved to Bengaluru and established{" "}
                                <span className="text-maroon">
                                    Shri Vishwa Bandhu Sangeetha Gurukula
                                </span>
                                , through which he has imparted music education to hundreds
                                of students. He has performed across the country, served as a
                                member of the Karnataka Sangeetha Nritya Academy, and earned
                                the affection of scholars and connoisseurs alike.
                            </p>
                        </div>

                        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="card-cream p-6 md:col-span-2">
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                                    <div className="md:col-span-2 relative">
                                        <div className="absolute -inset-2 border border-gold/40 pointer-events-none" />
                                        <img
                                            src={ASSETS.founderWithGuru}
                                            alt="Shri Ananya Bhargava Bedur with his revered Guru Pt. Ganapati Bhat Hasanagi"
                                            data-testid="founder-with-guru-image"
                                            className="relative w-full h-auto object-cover shadow-[0_20px_45px_-20px_rgba(90,17,31,0.45)]"
                                        />
                                    </div>
                                    <div className="md:col-span-3">
                                        <div className="eyebrow text-gold">Guru-Shishya Parampara</div>
                                        <h4 className="font-display text-2xl text-maroon mt-2 leading-tight">
                                            With his revered Guru
                                            <span className="italic text-gold"> Pt. Ganapati Bhat Hasanagi</span>
                                        </h4>
                                        <p className="font-serif text-base lg:text-[17px] text-ink/85 mt-4 leading-relaxed">
                                            Shri Ananya Bhargava Bedur, Founder of Shri Vishwa Bandhu
                                            Sangeetha Gurukula, pictured with his revered Guru,
                                            Pt. Ganapati Bhat Hasanagi — an unbroken thread of the
                                            Kirana gharana carried forward into the Gurukula's teaching today.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="card-cream p-6">
                                <div className="eyebrow text-gold">Cinema Legacy</div>
                                <p className="font-serif text-base lg:text-[17px] text-ink/85 mt-3 leading-relaxed">
                                    The famous Kannada actor, the late{" "}
                                    <span className="text-maroon">Dr. Vishnuvardhan</span>,
                                    was learning music from Ananya during his final days — a
                                    matter of immense pride for the Gurukula.
                                </p>
                            </div>
                            <div className="card-cream p-6">
                                <div className="eyebrow text-gold">ISRO & Dr. Kalam</div>
                                <p className="font-serif text-base lg:text-[17px] text-ink/85 mt-3 leading-relaxed">
                                    He judged a music competition for ISRO scientists during
                                    the 2008 Chandrayaan celebrations, and performed before
                                    the late{" "}
                                    <span className="text-maroon">Dr. A.P.J. Abdul Kalam</span>{" "}
                                    at the Satellite Control Centre, Byalalu.
                                </p>
                            </div>
                            <div className="card-cream p-6 md:col-span-2">
                                <div className="eyebrow text-gold">Yantra Rishi</div>
                                <p className="font-serif text-base lg:text-[17px] text-ink/85 mt-3 leading-relaxed">
                                    Composed music for the first-ever Kannada song on{" "}
                                    <span className="text-maroon">Sir M. Visvesvaraya</span>,
                                    titled <em>“Yantra Rishi”</em> — composing, singing
                                    lyrics by eminent poets and bringing together renowned
                                    Kannada artists.
                                </p>
                            </div>
                        </div>

                        <div className="mt-10">
                            <div className="eyebrow">Albums released</div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                {albums.map((a) => (
                                    <span
                                        key={a}
                                        className="font-serif italic text-maroon border border-gold-100 px-4 py-1.5 bg-cream"
                                    >
                                        {a}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <OrnamentalDivider className="mt-24" />
            </div>
        </section>
    );
}
